import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftSnackbar from "components/SoftSnackbar";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import FlagLoader from "layouts/flagloader";

import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import { useProjectPermissions } from "../projectPermissions";
import { ProjectCommunicationComposerDialog } from "../components/ProjectCommunicationDialog";
import ProjectLiveChatBadge from "layouts/project-live-chat/ProjectLiveChatBadge";
import { useProjectLiveChat } from "layouts/project-live-chat/useProjectLiveChat";
import StatsHoverTooltip from "../components/StatsHoverTooltip";
import CompleteDeviceMetric from "../CompleteDeviceMetric";
import ProjectTableSortLabel, {
  epcSortValue,
  sortTableRows,
} from "../components/ProjectTableSortLabel";
import { hasPermission } from "utils/granularPermissions";
import {
  projectFilterSearchSuffix,
  projectFilterStateFromSearch,
  writeProjectFiltersToParams,
} from "../projectFilterQuery";

const liveStatuses = ["soft_launch", "full_launch", "hard_launch", "live"];

const exportFilenamePart = (value) =>
  String(value || "project-vendors")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "project-vendors";

const vendorStatusActions = [
  { value: "active", label: "Active", color: "#16a34a" },
  { value: "inactive", label: "Inactive", color: "#dc2626" },
];

const tableHeaderCellSx = {
  backgroundColor: "#f8f9fa",
  color: "#8392ab",
  fontSize: "12px",
  fontWeight: 400,
  letterSpacing: 0,
  textTransform: "capitalize",
  borderBottom: "1px solid #e9ecef",
  py: 0.75,
  px: 0.35,
  whiteSpace: "normal",
  lineHeight: 1.15,
  verticalAlign: "middle",
};

const tableBodyCellSx = {
  color: "#334155",
  fontSize: "10px",
  fontWeight: 400,
  borderBottom: "1px solid #edf2f7",
  py: 0.4,
  px: 0.35,
  verticalAlign: "middle",
  lineHeight: 1.15,
  overflowWrap: "anywhere",
};

const metricHeaderCellSx = {
  ...tableHeaderCellSx,
  px: 0.15,
  py: 0.55,
  textAlign: "center",
  borderLeft: "1px solid #e9ecef",
};

const metricBodyCellSx = {
  ...tableBodyCellSx,
  px: 0.1,
  py: 0.35,
  textAlign: "center",
  whiteSpace: "nowrap",
  borderLeft: "1px solid #edf2f7",
};

const listSearchInputSx = {
  minHeight: "24px !important",
  padding: "1px 8px !important",
  "& input": {
    height: "20px !important",
    fontSize: "10px !important",
  },
  "& input[type='date']": {
    lineHeight: "20px !important",
  },
  "& input[type='date']::-webkit-calendar-picker-indicator": {
    width: "12px",
    height: "12px",
    padding: 0,
    margin: 0,
  },
};

const listSearchControlSx = {
  width: { xs: "100%", sm: "9rem" },
  flex: { xs: "1 1 100%", sm: "0 0 9rem" },
};

const listSearchSelectSx = {
  "--soft-select-height": "24px",
  "--soft-select-value-height": "22px",
  "--soft-select-font-size": "10px",
  "--soft-select-value-px": "8px",
  "--soft-select-text-top": "50%",
  "--soft-select-indicator-padding": "3px",
  "--soft-select-indicator-size": "12px",
  "--soft-select-menu-padding": "4px",
  "--soft-select-option-padding": "4px 8px",
  "--soft-select-option-min-height": "24px",
  "--soft-select-option-font-size": "10px",
};

const overviewLabelSx = {
  display: "block",
  color: "#1e293b",
  fontSize: "10px",
  fontWeight: 700,
};

const overviewValueSx = {
  color: "#64748b",
  fontSize: "10px",
  lineHeight: 1.25,
  minHeight: 15,
  overflowWrap: "anywhere",
};

const overviewButtonSx = {
  minHeight: 26,
  height: 26,
  px: 1.25,
  py: 0,
  fontSize: "10px",
  lineHeight: 1,
  textTransform: "none",
  borderRadius: "6px",
  whiteSpace: "nowrap",
  "& .MuiIcon-root": {
    fontSize: "14px !important",
  },
};

const emptyBreakdown = {
  "1h": { complete: 0, clicks: 0, v_rtr: null },
  "12h": { complete: 0, clicks: 0, v_rtr: null },
  "24h": { complete: 0, clicks: 0, v_rtr: null },
  overall: { complete: 0, clicks: 0, v_rtr: null },
};

const textOrDash = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const htmlToPlainText = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const formatIrDisplay = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  const whole = Math.floor(number);
  return number - whole > 0.5 ? whole + 1 : whole;
};

const numericValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const epcText = (revenue, breakdown, stats = {}) => {
  const overall = breakdown?.overall || {};
  const clicks = numericValue(overall.clicks ?? stats.total);
  const totalRevenue = numericValue(revenue);

  if (clicks === null || clicks <= 0 || totalRevenue === null) return "-";
  return `$${(totalRevenue / clicks).toFixed(2)}`;
};

const roundedMetricText = (value) => {
  const number = numericValue(value);
  if (number === null) return "-";
  const whole = Math.floor(number);
  return number - whole > 0.5 ? whole + 1 : whole;
};

const loiMetricColor = (average, configured) => {
  const averageValue = numericValue(average);
  const configuredValue = numericValue(configured);

  if (averageValue === null || configuredValue === null) return "#334155";
  return Math.abs(averageValue - configuredValue) <= 5 ? "#15803d" : "#dc2626";
};

const irMetricColor = (average, configured) => {
  const actualValue = numericValue(average);
  const configuredValue = numericValue(configured);

  if (actualValue === null || configuredValue === null) return "#334155";
  return Math.abs(actualValue - configuredValue) <= 10 ? "#15803d" : "#dc2626";
};

function IrMetric({ average, configured }) {
  return (
    <SoftBox component="span" sx={{ color: "#334155", fontWeight: 400 }}>
      <SoftBox
        component="span"
        sx={{
          color: irMetricColor(average, configured),
          fontWeight: numericValue(average) === null ? 400 : 700,
        }}
      >
        {formatIrDisplay(average)}
      </SoftBox>
      /{formatIrDisplay(configured)}
    </SoftBox>
  );
}

IrMetric.propTypes = {
  average: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  configured: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

function LoiMetric({ average, configured }) {
  return (
    <SoftBox component="span" sx={{ color: "#334155", fontWeight: 400 }}>
      <SoftBox
        component="span"
        sx={{
          color: loiMetricColor(average, configured),
          fontWeight: numericValue(average) === null ? 400 : 700,
        }}
      >
        {roundedMetricText(average)}
      </SoftBox>
      /{roundedMetricText(configured)}
    </SoftBox>
  );
}

LoiMetric.propTypes = {
  average: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  configured: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const digitsOnly = (value, maxDigits = 7) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, maxDigits);

const decimalInput = (value, integerDigits = 7, decimalDigits = 2) => {
  const cleaned = String(value || "").replace(/[^\d.]/g, "");
  const [integer = "", ...decimalParts] = cleaned.split(".");
  const nextInteger = integer.slice(0, integerDigits);
  const decimal = decimalParts.join("").slice(0, decimalDigits);

  if (cleaned.includes(".")) return `${nextInteger}${nextInteger ? "." : ""}${decimal}`;
  return nextInteger;
};

const isValidRequiredCpi = (value, minimum = 1) => {
  const text = String(value ?? "").trim();
  const amount = Number(text);

  return (
    text !== "" &&
    Number.isFinite(amount) &&
    amount >= minimum &&
    /^\d+(?:\.\d{1,2})?$/.test(text)
  );
};

const quotaText = (value) => {
  const quota = Number(value || 0);
  return Number.isFinite(quota) && quota > 0 ? quota : "QS";
};

const currencyText = (value) => {
  const amount = Number(value || 0);
  return `$${amount.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};

const percentageText = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const percentage = Number(value);
  return Number.isFinite(percentage) ? `${percentage.toFixed(2)}%` : "-";
};

const vendorRedirectFields = [
  { key: "complete_redirect_url", label: "Complete Redirect (Status 1)" },
  { key: "terminate_redirect_url", label: "Terminate Redirect (Status 2)" },
  { key: "over_quota_redirect_url", label: "Over Quota Redirect (Status 3)" },
  { key: "quality_terminate_redirect_url", label: "Quality Terminate Redirect (Status 4)" },
];

const vendorRedirectValues = (source = {}) =>
  vendorRedirectFields.reduce(
    (values, field) => ({
      ...values,
      [field.key]: source?.[field.key] || "",
    }),
    {}
  );

const emptyVendorForm = () => ({
  vendor: null,
  vendor_quota: "",
  vendor_clicks_quota: "",
  target_cpi: "",
  feasibility: "",
  required_field_time_days: "",
  is_verisoul_security: false,
  link_hit_gap_seconds: "60",
  ...vendorRedirectValues(),
  redirect_baseline: vendorRedirectValues(),
});

const vendorRedirectsChanged = (form) =>
  vendorRedirectFields.some(
    (field) => (form?.[field.key] || "") !== (form?.redirect_baseline?.[field.key] || "")
  );

const vendorToOption = (vendor) => {
  const value = vendor?.value ?? vendor?.id;
  if (!value) return null;

  const name = vendor?.name || vendor?.company_name || vendor?.company || `Vendor ${value}`;

  return {
    ...vendor,
    value,
    id: value,
    label: name,
    name,
  };
};

const selectOptionValue = (option) => option?.value ?? option?.id ?? "";

const dateOnlyValue = (value) => {
  if (!value) return "";

  const text = String(value);
  const directDate = text.match(/^(\d{4}-\d{2}-\d{2})/);
  if (directDate) return directDate[1];

  const date = new Date(text.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return "";

  const pad = (item) => String(item).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const filterOptionFromValue = (value, label) => {
  if (value === null || value === undefined || value === "" || !label) return null;

  return {
    value: String(value),
    label: String(label),
    name: String(label),
  };
};

const selectedFilterOptionMatches = (selected, rowOption) => {
  const selectedOptions = Array.isArray(selected) ? selected : selected ? [selected] : [];
  if (!selectedOptions.length) return true;
  if (!rowOption) return false;
  const rowValue = String(rowOption.value || "");
  const rowLabel = String(rowOption.label || rowOption.name || "").toLowerCase();

  return selectedOptions.some((option) => {
    const selectedValue = String(option?.value ?? option?.id ?? "");
    const selectedLabel = String(option?.label || option?.name || "").toLowerCase();
    return rowValue === selectedValue || (selectedLabel && rowLabel === selectedLabel);
  });
};

const mergeFilterOptions = (options, selected) => {
  const merged = [];

  [...(Array.isArray(selected) ? selected : selected ? [selected] : []), ...(Array.isArray(options) ? options : [])].forEach((option) => {
    const normalized = filterOptionFromValue(
      option?.value ?? option?.id,
      option?.label ?? option?.name
    );
    if (!normalized) return;

    if (!merged.some((item) => String(item.value) === String(normalized.value))) {
      merged.push(normalized);
    }
  });

  return merged;
};

const projectCreatedDateMatches = (project, startDate, endDate) => {
  if (!startDate && !endDate) return true;

  const dateValue = dateOnlyValue(project?.created_at || project?.bid_creation_date);
  if (!dateValue) return false;
  if (startDate && dateValue < startDate) return false;
  if (endDate && dateValue > endDate) return false;

  return true;
};

const projectTabFromStatus = (status) => {
  const normalized = String(status || "won").toLowerCase();
  if (liveStatuses.includes(normalized)) return "live";
  return ["pause", "invoiced", "invoice_at_zero", "closed"].includes(normalized)
    ? normalized
    : "won";
};

function ProjectCountryVendors() {
  const { id, country: countryParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions = [], userPermission = [] } = useAuth();
  const projectPermissions = useProjectPermissions(permissions, userPermission);
  const { canUseProjectLiveChat, openProjectLiveChat } = useProjectLiveChat();
  const token = localStorage.getItem("authToken");
  const initialProjectFilters = projectFilterStateFromSearch(location.search);
  const canViewProjects = projectPermissions.canViewPage;
  const canUseActions = projectPermissions.canField("actions");
  const canSearchProjects = projectPermissions.canAction("search");
  const canExportProjects = projectPermissions.canAction("export");
  const canOpenAddVendor = projectPermissions.canAction("open_add_vendor");
  const canSubmitAddVendor = projectPermissions.canAction("submit_add_vendor");
  const canOpenVendorDetails = projectPermissions.canAction("open_vendor_details");
  const canSubmitVendorDetails = projectPermissions.canAction("submit_vendor_details");
  const canManageVendorLinkHitGap = projectPermissions.canAction(
    "manage_vendor_link_hit_gap"
  );
  const canUpdateVendorStatus = projectPermissions.canAction("update_vendor_status");
  const canCopyVendorLinks = projectPermissions.canAction("copy_vendor_links");
  const canViewSurveyResponses = projectPermissions.canAction("view_survey_responses");
  const canViewCountrySurveyResponses =
    canViewSurveyResponses &&
    hasPermission(
      projectPermissions.grantedPermissions,
      "projects.survey_responses.pages.country.view"
    );
  const canViewVendorSurveyResponses =
    canViewSurveyResponses &&
    hasPermission(
      projectPermissions.grantedPermissions,
      "projects.survey_responses.pages.vendor.view"
    );
  const canViewVendorUrl = projectPermissions.canAction("view_vendor_url");
  const canCopyVendorUrl = projectPermissions.canAction("copy_vendor_url");
  const canViewProjectCode = projectPermissions.canField("project_code");
  const canViewProjectTitle = projectPermissions.canField("project_title");
  const canViewClient = projectPermissions.canField("client");
  const canViewProjectManager = projectPermissions.canField("project_manager");
  const canViewStudyType = projectPermissions.canField("study_type");
  const canViewProjectType = projectPermissions.canField("project_type");
  const canViewCreatedDate = projectPermissions.canField("created_at");
  const canViewCountry = projectPermissions.canField("country");
  const canViewCountryProjectName = projectPermissions.canField("country_project_name");
  const canViewVendors = projectPermissions.canField("vendors");
  const canViewRequiredN = projectPermissions.canField("required_n");
  const canViewVendorQuota = projectPermissions.canField("vendor_quota");
  const canViewIr = projectPermissions.canField("ir");
  const canViewLoi = projectPermissions.canField("loi");
  const canViewCpi = projectPermissions.canField("cpi");
  const canViewTargetCpi = projectPermissions.canField("target_cpi");
  const canViewFeasibility = projectPermissions.canField("feasibility");
  const canViewRequiredFieldTime = projectPermissions.canField("required_field_time_days");
  const canViewStats = projectPermissions.canField("stats");
  const canViewCompleteDeviceBrowser = projectPermissions.canField("complete_device_browser_counting");
  const canViewRtr = projectPermissions.canField("rtr");
  const canViewRevenue = projectPermissions.canField("revenue");
  const canViewStatus = projectPermissions.canField("status");
  const canViewComments = projectPermissions.canField("comments");
  const canViewTargeting = projectPermissions.canField("targeting");
  const canViewLiveUrl = projectPermissions.canField("live_url");
  const canViewTestUrl = projectPermissions.canField("test_url");
  const canViewVendorRedirectDetails = projectPermissions.canField("vendor_url") && canViewVendorUrl;

  const [bid, setBid] = useState(null);
  const [country, setCountry] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(initialProjectFilters.search);
  const [listStartDate, setListStartDate] = useState(initialProjectFilters.startDate);
  const [listEndDate, setListEndDate] = useState(initialProjectFilters.endDate);
  const [studyTypeFilter, setStudyTypeFilter] = useState(initialProjectFilters.studyType);
  const [projectTypeFilter, setProjectTypeFilter] = useState(initialProjectFilters.projectType);
  const [clientFilter, setClientFilter] = useState(initialProjectFilters.client);
  const [projectClientFilterOptions, setProjectClientFilterOptions] = useState([]);
  const [projectClientFilterOptionsLoading, setProjectClientFilterOptionsLoading] = useState(false);
  const [expandedVendorIds, setExpandedVendorIds] = useState([]);
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [actionVendor, setActionVendor] = useState(null);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [vendorDialogMode, setVendorDialogMode] = useState("edit");
  const [vendorDialogVendor, setVendorDialogVendor] = useState(null);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorOptionsLoading, setVendorOptionsLoading] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkDialogVendor, setLinkDialogVendor] = useState(null);
  const [vendorForm, setVendorForm] = useState(emptyVendorForm());
  const [addVendorForms, setAddVendorForms] = useState([emptyVendorForm()]);
  const [quotaSubmitLoading, setQuotaSubmitLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [communicationDialog, setCommunicationDialog] = useState(null);

  const sourceProjectTab =
    new URLSearchParams(location.search).get("tab") ||
    bid?.project_tab ||
    projectTabFromStatus(bid?.status);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    }),
    [token]
  );

  const showMessage = useCallback((message, color = "success") => {
    setSnackbar({ open: true, message, color });
  }, []);

  const fetchProjectClientFilterOptions = useCallback(async () => {
    if (!token || !canViewClient || !sourceProjectTab) {
      setProjectClientFilterOptions([]);
      return;
    }

    try {
      setProjectClientFilterOptionsLoading(true);
      const params = new URLSearchParams({
        project_filter: "1",
        project_tab: sourceProjectTab,
      });
      const response = await fetch(`${API_URL}/projects/clients?${params.toString()}`, {
        headers: authHeaders,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to fetch project client filters");
      }

      setProjectClientFilterOptions(
        (Array.isArray(data.clients) ? data.clients : [])
          .map((client) =>
            filterOptionFromValue(
              client?.value ?? client?.id,
              client?.label || client?.name || client?.code
            )
          )
          .filter(Boolean)
      );
    } catch (error) {
      setProjectClientFilterOptions([]);
      showMessage(error.message || "Failed to fetch project client filters", "error");
    } finally {
      setProjectClientFilterOptionsLoading(false);
    }
  }, [authHeaders, canViewClient, showMessage, sourceProjectTab, token]);

  const projectFilterVisibility = useMemo(
    () => ({
      canSearch: canSearchProjects,
      canViewCreatedDate,
      canViewStudyType,
      canViewProjectType,
      canViewClient,
    }),
    [canSearchProjects, canViewClient, canViewCreatedDate, canViewProjectType, canViewStudyType]
  );

  const currentProjectFilters = useMemo(
    () => ({
      search: searchInput,
      startDate: listStartDate,
      endDate: listEndDate,
      studyType: studyTypeFilter,
      projectType: projectTypeFilter,
      client: clientFilter,
    }),
    [clientFilter, listEndDate, listStartDate, projectTypeFilter, searchInput, studyTypeFilter]
  );

  const currentProjectFilterSuffix = useMemo(
    () => projectFilterSearchSuffix(currentProjectFilters, projectFilterVisibility),
    [currentProjectFilters, projectFilterVisibility]
  );

  const openVendorCommunicationDialog = useCallback(
    (type, vendor) => {
      if (!vendor?.country_win_id) {
        showMessage("Vendor project link is not available.", "error");
        return;
      }

      setCommunicationDialog({
        type,
        scope: {
          scopeType: "vendor",
          projectId: bid?.id || id,
          projectDetailId: country?.id || vendor.bid_detail_id,
          vendorCountryWinId: vendor.country_win_id,
          vendorId: vendor.id,
          projectCode: bid?.quest_code || id,
          projectName: bid?.project_title || bid?.quest_code,
          label: `Project: ${bid?.quest_code || id} / Country: ${country?.country_name || countryParam
            } / Vendor: ${vendor.name || vendor.company || vendor.id}`,
        },
      });
    },
    [
      bid?.id,
      bid?.project_title,
      bid?.quest_code,
      country?.country_name,
      country?.id,
      countryParam,
      id,
      showMessage,
    ]
  );

  const selectedDetailId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("detailId") || "";
  }, [location.search]);

  const vendorPageQuery = useMemo(
    () => (selectedDetailId ? `?detailId=${encodeURIComponent(selectedDetailId)}` : ""),
    [selectedDetailId]
  );

  const fetchVendorMetrics = useCallback(async () => {
    if (!canViewProjects || !token || !id || !countryParam) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/projects/${id}/countries/${encodeURIComponent(countryParam)}/vendor-metrics${vendorPageQuery}`,
        { headers: authHeaders }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to fetch country vendor metrics");
      }

      setBid(data.bid || null);
      setCountry(data.country || null);
      setVendors(Array.isArray(data.vendors) ? data.vendors : []);
    } catch (error) {
      showMessage(error.message || "Failed to fetch country vendor metrics", "error");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, canViewProjects, countryParam, id, showMessage, token, vendorPageQuery]);

  useEffect(() => {
    fetchVendorMetrics();
  }, [fetchVendorMetrics]);

  useEffect(() => {
    fetchProjectClientFilterOptions();
  }, [fetchProjectClientFilterOptions]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    writeProjectFiltersToParams(params, currentProjectFilters, projectFilterVisibility);

    const nextSearch = params.toString();
    const currentSearch = new URLSearchParams(location.search).toString();

    if (nextSearch === currentSearch) return;

    navigate(
      {
        pathname: location.pathname,
        search: `?${nextSearch}`,
      },
      { replace: true }
    );
  }, [
    currentProjectFilters,
    location.pathname,
    location.search,
    navigate,
    projectFilterVisibility,
  ]);

  const studyTypeText = bid?.project_type_master?.name || bid?.project_type || "";
  const projectTypeText =
    (typeof bid?.project_type_option === "object"
      ? bid.project_type_option?.name || bid.project_type_option?.label
      : bid?.project_type_option) || "";
  const clientFilterOption = filterOptionFromValue(
    bid?.client_id ?? bid?.client?.id ?? bid?.client_name ?? bid?.client?.name,
    bid?.client_name || bid?.client?.name || ""
  );
  const studyTypeFilterOption = filterOptionFromValue(
    bid?.project_type_id ?? bid?.project_type_master?.id ?? studyTypeText,
    studyTypeText
  );
  const projectTypeFilterOption = filterOptionFromValue(
    bid?.project_type_option_id ??
    (typeof bid?.project_type_option === "object" ? bid.project_type_option?.id : null) ??
    projectTypeText,
    projectTypeText
  );
  const studyTypeFilterOptions = useMemo(
    () => mergeFilterOptions(studyTypeFilterOption ? [studyTypeFilterOption] : [], studyTypeFilter),
    [studyTypeFilter, studyTypeFilterOption]
  );
  const projectTypeFilterOptions = useMemo(
    () => mergeFilterOptions(projectTypeFilterOption ? [projectTypeFilterOption] : [], projectTypeFilter),
    [projectTypeFilter, projectTypeFilterOption]
  );
  const clientFilterOptions = useMemo(
    () =>
      mergeFilterOptions(
        [...projectClientFilterOptions, ...(clientFilterOption ? [clientFilterOption] : [])],
        clientFilter
      ),
    [clientFilter, clientFilterOption, projectClientFilterOptions]
  );
  const projectFiltersMatch =
    (!canViewCreatedDate || projectCreatedDateMatches(bid, listStartDate, listEndDate)) &&
    (!canViewStudyType || selectedFilterOptionMatches(studyTypeFilter, studyTypeFilterOption)) &&
    (!canViewProjectType || selectedFilterOptionMatches(projectTypeFilter, projectTypeFilterOption)) &&
    (!canViewClient || selectedFilterOptionMatches(clientFilter, clientFilterOption));

  const filteredVendors = useMemo(() => {
    if (!projectFiltersMatch) return [];
    if (!canSearchProjects) return vendors;

    const search = searchInput.trim().toLowerCase();
    if (!search) return vendors;

    return vendors.filter((vendor) =>
      [
        vendor.name,
        vendor.company,
        vendor.company_name,
        vendor.email,
        vendor.vendor_quota,
        vendor.vendor_clicks_quota,
        country?.name,
        country?.country_name,
        country?.country_code,
        bid?.quest_code,
        bid?.project_title,
        bid?.client_name,
        bid?.client?.name,
        studyTypeText,
        projectTypeText,
      ]
        .some((value) => String(value ?? "").toLowerCase().includes(search))
    );
  }, [
    bid,
    canSearchProjects,
    country,
    projectFiltersMatch,
    projectTypeText,
    searchInput,
    studyTypeText,
    vendors,
  ]);

  const handleSort = useCallback((key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  const sortedVendors = useMemo(
    () =>
      sortTableRows(filteredVendors, sortConfig, (vendor, key) => {
        const stats = vendor.stats || {};

        switch (key) {
          case "vendor":
            return vendor.name || vendor.company || vendor.code;
          case "required_n":
            return numericValue(country?.required_n);
          case "vendor_quota":
            return numericValue(vendor.vendor_quota);
          case "vendor_clicks_quota":
            return numericValue(vendor.vendor_clicks_quota);
          case "ir":
            return numericValue(vendor.average_ir) ?? numericValue(country?.ir);
          case "loi":
            return numericValue(vendor.average_loi) ?? numericValue(country?.loi);
          case "cpi":
            return numericValue(vendor.won_target_cpi);
          case "device_browser":
          case "complete":
            return Number(stats.complete || 0);
          case "terminate":
            return Number(stats.terminate || 0);
          case "quota_full":
            return Number(stats.quota_full || 0);
          case "security_terminate":
            return Number(stats.security_terminate || 0);
          case "initiate":
            return Number(stats.initiate || 0);
          case "vm":
            return numericValue(vendor.rtr);
          case "epc":
            return epcSortValue(vendor.revenue, vendor.breakdown, stats);
          case "revenue":
            return numericValue(vendor.revenue);
          case "links":
            return vendor.live_url || vendor.test_url;
          case "status":
            return vendor.status;
          default:
            return null;
        }
      }),
    [country, filteredVendors, sortConfig]
  );

  const normalizedProjectStatus = String(bid?.status || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  const isLiveProjectTab =
    String(bid?.project_tab || "").toLowerCase() === "live" ||
    liveStatuses.includes(normalizedProjectStatus);
  const isWonProject =
    String(sourceProjectTab).toLowerCase() === "won" ||
    projectTabFromStatus(bid?.status) === "won";
  const showEpc = canViewRtr && !isWonProject;
  const canShowVendorLiveLinks = isLiveProjectTab && canViewVendorUrl;
  const canAddVendor = canUseActions && canOpenAddVendor && canShowVendorLiveLinks;
  const canEditVendorDetails = canUseActions && canOpenVendorDetails && canShowVendorLiveLinks;
  const showVendorRowActions =
    canUseProjectLiveChat ||
    (canUseActions &&
      (canViewComments ||
        projectPermissions.canAction("mail") ||
        projectPermissions.canAction("chat") ||
        canEditVendorDetails ||
        canViewVendorSurveyResponses));
  const showVendorStatusActions = canUseActions && canUpdateVendorStatus;
  const canSubmitVendorDialog =
    vendorDialogMode === "add" ? canSubmitAddVendor : canSubmitVendorDetails;
  const vendorColumnCount =
    [
      canViewVendors,
      canViewRequiredN,
      canViewVendorQuota,
      canViewVendorQuota,
      canViewIr,
      canViewLoi,
      canViewCpi,
      canViewRtr,
      showEpc,
      canViewRevenue,
      canShowVendorLiveLinks,
      canViewStatus,
      showVendorRowActions,
      showVendorStatusActions,
    ].filter(Boolean).length +
    (canViewStats ? 5 : 0) +
    (canViewCompleteDeviceBrowser ? 1 : 0);
  const vendorsWithLinks = useMemo(
    () => vendors.filter((vendor) => vendor?.live_url || vendor?.test_url),
    [vendors]
  );

  const vendorDisplayName = (vendor) =>
    vendor?.name || vendor?.company || `Vendor ${vendor?.id || ""}`;

  const openSurveyResponses = useCallback(
    (vendorId = "") => {
      if (vendorId ? !canViewVendorSurveyResponses : !canViewCountrySurveyResponses) return;
      const detailQuery = selectedDetailId
        ? `&detailId=${encodeURIComponent(selectedDetailId)}`
        : "";
      const basePath = `/projects/${id}/countries/${encodeURIComponent(
        countryParam
      )}/survey-responses`;
      const vendorPath = vendorId
        ? `/projects/${id}/countries/${encodeURIComponent(
          countryParam
        )}/vendors/${encodeURIComponent(vendorId)}/survey-responses`
        : basePath;

      navigate(
        `${vendorPath}?tab=${encodeURIComponent(sourceProjectTab)}${currentProjectFilterSuffix}${detailQuery}`
      );
    },
    [
      canViewCountrySurveyResponses,
      canViewVendorSurveyResponses,
      countryParam,
      currentProjectFilterSuffix,
      id,
      navigate,
      selectedDetailId,
      sourceProjectTab,
    ]
  );

  const buildVendorLinksText = () =>
    vendorsWithLinks
      .map(
        (vendor, index) =>
          `${index + 1}. ${vendorDisplayName(vendor)}\nProject Name: ${country?.country_project_name || bid?.project_title || "-"
          }\nLink Id: ${vendor.link_id || "-"}\nLive Url: ${vendor.live_url || "-"
          }\nTest Url: ${vendor.test_url || "-"}`
      )
      .join("\n\n");

  const toggleVendorDetails = (vendorId) => {
    setExpandedVendorIds((current) =>
      current.includes(vendorId)
        ? current.filter((currentId) => currentId !== vendorId)
        : [...current, vendorId]
    );
  };

  const vendorStatusBadge = (vendor) => {
    const status = String(vendor?.status || "active").toLowerCase() === "inactive" ? "inactive" : "active";
    const style =
      status === "inactive"
        ? { color: "#dc2626", background: "#fee2e2", border: "#fecaca" }
        : { color: "#16a34a", background: "#dcfce7", border: "#86efac" };

    return (
      <SoftBox
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 20,
          px: 0.65,
          borderRadius: "999px",
          color: style.color,
          backgroundColor: style.background,
          border: `1px solid ${style.border}`,
          fontSize: 10,
          fontWeight: 400,
          lineHeight: 1.1,
          whiteSpace: "normal",
          textAlign: "center",
        }}
      >
        {status === "inactive" ? "Inactive" : "Active"}
      </SoftBox>
    );
  };

  const projectStatusBadge = () => {
    const status = String(country?.status || bid?.status || "").toLowerCase();
    const label = country?.status_label || bid?.project_status_label || status;
    const styles = {
      soft_launch: { color: "#b45309", background: "#fef3c7", border: "#fcd34d" },
      full_launch: { color: "#15803d", background: "#dcfce7", border: "#86efac" },
      hard_launch: { color: "#6d28d9", background: "#ede9fe", border: "#c4b5fd" },
      live: { color: "#be123c", background: "#ffe4e6", border: "#fda4af" },
      pause: { color: "#b45309", background: "#fff7ed", border: "#fdba74" },
    };
    const style = styles[status] || {
      color: "#475569",
      background: "#f1f5f9",
      border: "#cbd5e1",
    };

    return (
      <SoftBox
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          minHeight: 20,
          px: 0.65,
          borderRadius: "999px",
          color: style.color,
          backgroundColor: style.background,
          border: `1px solid ${style.border}`,
          fontSize: 10,
          lineHeight: 1.1,
        }}
      >
        {textOrDash(label)}
      </SoftBox>
    );
  };

  const selectedCountryName = country?.country_name || country?.name || "";
  const selectedCountryCode = country?.country_code || country?.code || "";
  const countryNameWithCode =
    selectedCountryName && selectedCountryCode
      ? `${selectedCountryName} (${selectedCountryCode})`
      : selectedCountryName || selectedCountryCode;
  const overviewItems = [
    canViewProjectCode ? { label: "Project Code", value: bid?.quest_code } : null,
    canViewProjectTitle ? { label: "Project Name", value: bid?.project_title } : null,
    canViewClient ? { label: "Client", value: bid?.client_name || bid?.client?.name } : null,
    canViewProjectManager
      ? { label: "Project Manager", value: bid?.project_manager?.name || bid?.project_manager?.email }
      : null,
    canViewStatus ? { label: "Project Status", value: projectStatusBadge() } : null,
    canViewStudyType ? { label: "Study Type", value: studyTypeText } : null,
    canViewProjectType ? { label: "Project Type", value: projectTypeText } : null,
    canViewRequiredN ? { label: "Required N", value: country?.required_n } : null,
    canViewIr ? { label: "IR(%)", value: formatIrDisplay(country?.ir) } : null,
    canViewLoi ? { label: "LOI(mins)", value: roundedMetricText(country?.loi) } : null,
    canViewCpi
      ? {
        label: "CPI",
        value:
          country?.won_cpi === null ||
            country?.won_cpi === undefined ||
            country?.won_cpi === ""
            ? "-"
            : `$${country.won_cpi}`,
      }
      : null,
    canViewTargeting
      ? { label: "Targeting", value: htmlToPlainText(country?.targeting) || "No Targeting" }
      : null,
    canViewCountry ? { label: "Country Name", value: countryNameWithCode } : null,
    canViewCountryProjectName
      ? { label: "Country Project Name", value: country?.country_project_name }
      : null,
  ].filter(Boolean);

  const openVendorActionMenu = (event, vendor) => {
    if (!canUpdateVendorStatus) return;
    setActionsAnchor(event.currentTarget);
    setActionVendor(vendor);
  };

  const closeVendorActionMenu = () => {
    if (statusLoading) return;
    setActionsAnchor(null);
    setActionVendor(null);
  };

  const updateVendorStatus = async (status) => {
    if (!canUpdateVendorStatus) return;
    if (!bid?.id || !actionVendor?.country_win_id) return;

    try {
      setStatusLoading(true);
      const response = await fetch(
        `${API_URL}/projects/${bid.id}/vendor-country-wins/${actionVendor.country_win_id}/status`,
        {
          method: "PATCH",
          headers: authHeaders,
          body: JSON.stringify({ status }),
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to update vendor status");
      }

      setActionsAnchor(null);
      setActionVendor(null);
      showMessage(data.message || "Vendor status updated successfully", "success");
      fetchVendorMetrics();
    } catch (error) {
      showMessage(error.message || "Failed to update vendor status", "error");
    } finally {
      setStatusLoading(false);
    }
  };

  const fetchVendorOptions = useCallback(async () => {
    if (!token || !id) return;

    try {
      setVendorOptionsLoading(true);
      const response = await fetch(
        `${API_URL}/projects/${id}/available-vendors?exclude_won=0&exclude_sent_rfq=0&external_only=1&limit=500`,
        { headers: authHeaders }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to fetch vendors");
      }

      const existingVendorIds = new Set(
        vendors.map((vendor) => String(vendor?.id ?? vendor?.vendor_id ?? "")).filter(Boolean)
      );
      const options = (Array.isArray(data.vendors) ? data.vendors : [])
        .filter((vendor) => String(vendor?.vendor_type || "external").toLowerCase() !== "internal")
        .filter((vendor) => !existingVendorIds.has(String(vendor?.id ?? vendor?.value ?? "")))
        .map(vendorToOption)
        .filter(Boolean);

      setVendorOptions(options);
    } catch (error) {
      showMessage(error.message || "Failed to fetch vendors", "error");
    } finally {
      setVendorOptionsLoading(false);
    }
  }, [authHeaders, id, showMessage, token, vendors]);

  const openAddVendorDialog = () => {
    if (!canAddVendor) {
      showMessage("Vendors can only be added when the project is in a live status.", "error");
      return;
    }

    setVendorDialogMode("add");
    setVendorDialogVendor(null);
    setAddVendorForms([
      {
        ...emptyVendorForm(),
        is_verisoul_security: Boolean(country?.is_verisoul_security),
      },
    ]);
    fetchVendorOptions();
    setVendorDialogOpen(true);
  };

  const openVendorQuotaDialog = (vendor) => {
    if (!canEditVendorDetails) return;
    if (!vendor?.country_win_id) {
      showMessage("The won row for this vendor was not found, so quota cannot be edited.", "error");
      return;
    }

    setVendorDialogMode("edit");
    setVendorDialogVendor(vendor);
    setVendorForm({
      vendor: null,
      vendor_quota: vendor?.vendor_quota ?? "",
      vendor_clicks_quota: vendor?.vendor_clicks_quota ?? "",
      target_cpi: vendor?.won_target_cpi ?? "",
      feasibility: vendor?.won_feasibility ?? "",
      required_field_time_days: vendor?.won_required_field_time_days ?? "",
      is_verisoul_security: Boolean(vendor?.is_verisoul_security),
      link_hit_gap_seconds:
        vendor?.link_hit_gap_seconds === null || vendor?.link_hit_gap_seconds === undefined
          ? ""
          : String(vendor.link_hit_gap_seconds),
      ...vendorRedirectValues(vendor?.redirects || {}),
      redirect_baseline: vendorRedirectValues(vendor?.redirects || {}),
    });
    setVendorDialogOpen(true);
  };

  const closeVendorQuotaDialog = () => {
    if (quotaSubmitLoading) return;
    setVendorDialogOpen(false);
    setVendorDialogMode("edit");
    setVendorDialogVendor(null);
    setAddVendorForms([emptyVendorForm()]);
  };

  const openVendorLinksDialog = (vendor) => {
    if (!canViewVendorUrl) return;
    if (!vendor?.live_url && !vendor?.test_url) {
      showMessage("Live/test links have not been generated for this vendor. Please check the survey mapping.", "error");
      return;
    }

    setLinkDialogVendor(vendor);
    setLinkDialogOpen(true);
  };

  const closeVendorLinksDialog = () => {
    setLinkDialogOpen(false);
    setLinkDialogVendor(null);
  };

  const copyVendorLinks = () => {
    if (!canCopyVendorLinks) return;
    if (!vendorsWithLinks.length) {
      showMessage("Vendor links are not available for this country.", "error");
      return;
    }

    copyLink(buildVendorLinksText(), "Vendor links");
  };

  const exportVendorMetrics = () => {
    if (!canExportProjects || exportLoading) return;
    if (!filteredVendors.length) {
      showMessage("No vendor data available to export.", "error");
      return;
    }

    try {
      setExportLoading(true);
      const columns = [
        canViewVendors
          ? { label: "Vendor", value: (vendor) => textOrDash(vendorDisplayName(vendor)) }
          : null,
        canViewRequiredN
          ? { label: "Required N", value: () => textOrDash(country?.required_n) }
          : null,
        canViewVendorQuota
          ? { label: "Vendor Completes Quota (Cap)", value: (vendor) => textOrDash(vendor.vendor_quota) }
          : null,
        canViewVendorQuota
          ? { label: "Vendor Clicks Quota (Cap)", value: (vendor) => textOrDash(vendor.vendor_clicks_quota) }
          : null,
        canViewIr
          ? {
            label: "IR(%)",
            value: (vendor) =>
              `${formatIrDisplay(vendor.average_ir)}/${formatIrDisplay(country?.ir)}`,
          }
          : null,
        canViewLoi
          ? {
            label: "LOI(mins)",
            value: (vendor) =>
              `${roundedMetricText(vendor.average_loi)}/${roundedMetricText(country?.loi)}`,
          }
          : null,
        canViewCpi
          ? {
            label: "CPI",
            value: (vendor) =>
              vendor.won_target_cpi === null ||
                vendor.won_target_cpi === undefined ||
                vendor.won_target_cpi === ""
                ? "-"
                : `$${vendor.won_target_cpi}`,
          }
          : null,
        ...(canViewStats
          ? [
            {
              label: "c/QS",
              value: (vendor) =>
                `${Number(vendor.stats?.complete || 0)}/${quotaText(vendor.vendor_quota)}`,
            },
            {
              label: "T",
              value: (vendor) => Number(vendor.stats?.terminate || 0),
            },
            {
              label: "OQ",
              value: (vendor) => Number(vendor.stats?.quota_full || 0),
            },
            {
              label: "ST",
              value: (vendor) => Number(vendor.stats?.security_terminate || 0),
            },
            {
              label: "P",
              value: (vendor) => Number(vendor.stats?.initiate || 0),
            },
          ]
          : []),
        canViewRtr
          ? { label: "V.M", value: (vendor) => percentageText(vendor.rtr) }
          : null,
        showEpc
          ? {
              label: "EPC",
              value: (vendor) => epcText(vendor.revenue, vendor.breakdown, vendor.stats),
            }
          : null,
        canViewRevenue
          ? { label: "Revenue", value: (vendor) => currencyText(vendor.revenue) }
          : null,
        canShowVendorLiveLinks
          ? { label: "Live URL", value: (vendor) => vendor.live_url || "-" }
          : null,
        canViewStatus
          ? {
            label: "Status",
            value: (vendor) =>
              String(vendor?.status || "active").toLowerCase() === "inactive"
                ? "Inactive"
                : "Active",
          }
          : null,
      ].filter(Boolean);
      const headers = columns.map((column) => column.label);
      const rows = filteredVendors.map((vendor) =>
        columns.map((column) => column.value(vendor))
      );
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      worksheet["!cols"] = headers.map((header, columnIndex) => ({
        wch: Math.min(
          40,
          Math.max(
            String(header).length + 2,
            ...rows.map((row) => String(row[columnIndex] ?? "").length + 2)
          )
        ),
      }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Vendors");

      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
      XLSX.writeFile(
        workbook,
        `project-${exportFilenamePart(bid?.quest_code)}-${exportFilenamePart(
          selectedCountryCode || selectedCountryName
        )}-vendors-${timestamp}.xlsx`
      );
      showMessage("Vendor metrics exported successfully.", "success");
    } catch (error) {
      showMessage(error.message || "Failed to export vendor metrics.", "error");
    } finally {
      setExportLoading(false);
    }
  };

  const copyLink = async (value, label) => {
    if (!value) {
      showMessage(`${label} Url is not available.`, "error");
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      showMessage(`${label} Url copied.`, "success");
    } catch (error) {
      showMessage(`Failed to copy ${label} Url.`, "error");
    }
  };

  const updateVendorForm = (field, value) => {
    const integerFields = [
      "vendor_quota",
      "vendor_clicks_quota",
      "feasibility",
      "required_field_time_days",
      "link_hit_gap_seconds",
    ];
    const nextValue = field === "target_cpi" ? decimalInput(value, 7, 2) : value;

    setVendorForm((current) => {
      if (field === "vendor") {
        const redirects = vendorRedirectValues(value?.redirects || value || {});
        return {
          ...current,
          vendor: value,
          ...redirects,
          redirect_baseline: redirects,
        };
      }

      return {
        ...current,
        [field]: integerFields.includes(field) ? digitsOnly(nextValue, 7) : nextValue,
      };
    });
  };

  const updateAddVendorForm = (index, field, value) => {
    const integerFields = ["vendor_quota", "vendor_clicks_quota", "link_hit_gap_seconds"];
    const nextValue = field === "target_cpi" ? decimalInput(value, 7, 2) : value;

    setAddVendorForms((current) =>
      current.map((form, formIndex) => {
        if (formIndex !== index) return form;

        if (field === "vendor") {
          const redirects = vendorRedirectValues(value?.redirects || value || {});
          return {
            ...form,
            vendor: value,
            ...redirects,
            redirect_baseline: redirects,
          };
        }

        return {
          ...form,
          [field]: integerFields.includes(field) ? digitsOnly(nextValue, 7) : nextValue,
        };
      })
    );
  };

  const addVendorFormRow = () => {
    setAddVendorForms((current) => [
      ...current,
      {
        ...emptyVendorForm(),
        is_verisoul_security: Boolean(country?.is_verisoul_security),
      },
    ]);
  };

  const removeVendorFormRow = (index) => {
    setAddVendorForms((current) =>
      current.length <= 1 ? current : current.filter((_, formIndex) => formIndex !== index)
    );
  };

  const submitVendorQuota = async () => {
    if (!bid?.id) return;

    const isAddMode = vendorDialogMode === "add";
    const canSubmitCurrentVendorDialog = isAddMode ? canSubmitAddVendor : canSubmitVendorDetails;
    if (!canSubmitCurrentVendorDialog) return;

    if (!isAddMode && !vendorDialogVendor?.country_win_id) return;

    try {
      setQuotaSubmitLoading(true);
      const forms = isAddMode ? addVendorForms : [vendorForm];

      if (isAddMode) {
        const selectedForms = forms.filter((form) => selectOptionValue(form.vendor));
        if (!selectedForms.length || selectedForms.length !== forms.length) {
          showMessage("Vendor selection is required for every row.", "error");
          return;
        }

        const vendorIds = selectedForms.map((form) => String(selectOptionValue(form.vendor)));
        if (new Set(vendorIds).size !== vendorIds.length) {
          showMessage("Please select each vendor only once.", "error");
          return;
        }
      }

      const clientCpiManaged = !isAddMode && Boolean(vendorDialogVendor?.client_cpi_managed);
      const cpiChanged = clientCpiManaged && String(vendorForm.target_cpi) !== String(vendorDialogVendor?.won_target_cpi ?? "");
      if ((!clientCpiManaged || cpiChanged) && forms.some((form) => !isValidRequiredCpi(form.target_cpi, clientCpiManaged ? 0 : 1))) {
        showMessage(
          clientCpiManaged
            ? "Target CPI must be zero or more, with a maximum of 2 decimal places."
            : "Target CPI is required, must be at least 1, and can have a maximum of 2 decimal places.",
          "error"
        );
        return;
      }

      const hasInvalidGap =
        canManageVendorLinkHitGap &&
        forms.some((form) => {
          const rawSeconds = String(form.link_hit_gap_seconds ?? "").trim();
          if (rawSeconds === "") return false;
          const seconds = Number(rawSeconds);

          return !Number.isInteger(seconds) || seconds < 1 || seconds > 86400;
        });
      if (hasInvalidGap) {
        showMessage(
          "Link hit gap must be blank or a whole number between 1 and 86400 seconds.",
          "error"
        );
        return;
      }

      const messages = [];
      for (const form of forms) {
        const rawPayload = {
          vendor_id: isAddMode ? selectOptionValue(form.vendor) : undefined,
          vendor_quota: form.vendor_quota === "" ? null : form.vendor_quota,
          vendor_clicks_quota:
            form.vendor_clicks_quota === "" ? null : form.vendor_clicks_quota,
          target_cpi: form.target_cpi === "" ? null : form.target_cpi,
          is_verisoul_security: Boolean(form.is_verisoul_security),
        };
        if (canManageVendorLinkHitGap) {
          rawPayload.link_hit_gap_seconds =
            String(form.link_hit_gap_seconds ?? "").trim() === ""
              ? null
              : Number(form.link_hit_gap_seconds);
        }
        if (!isAddMode) {
          rawPayload.feasibility = form.feasibility === "" ? null : form.feasibility;
          rawPayload.required_field_time_days =
            form.required_field_time_days === "" ? null : form.required_field_time_days;
        }
        if (clientCpiManaged && !cpiChanged) delete rawPayload.target_cpi;
        const payload = projectPermissions.filterPayloadByFields(rawPayload);
        if (canManageVendorLinkHitGap) {
          payload.link_hit_gap_seconds = rawPayload.link_hit_gap_seconds;
        }
        // Unchecked on add means inherit country/project. During edit, only a
        // changed checkbox creates an explicit vendor ON/OFF override.
        if (
          (isAddMode &&
            rawPayload.is_verisoul_security !==
              Boolean(country?.is_verisoul_security)) ||
          (!isAddMode &&
            rawPayload.is_verisoul_security !==
              Boolean(vendorDialogVendor?.is_verisoul_security))
        ) {
          payload.is_verisoul_security = rawPayload.is_verisoul_security;
        }
        if (canViewVendorRedirectDetails && vendorRedirectsChanged(form)) {
          payload.redirects = vendorRedirectValues(form);
        }

        const response = await fetch(
          isAddMode
            ? `${API_URL}/projects/${bid.id}/countries/${encodeURIComponent(countryParam)}/vendor-country-wins${vendorPageQuery}`
            : `${API_URL}/projects/${bid.id}/vendor-country-wins/${vendorDialogVendor.country_win_id}/quota`,
          {
            method: isAddMode ? "POST" : "PATCH",
            headers: authHeaders,
            body: JSON.stringify(payload),
          }
        );
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.success !== 1) {
          throw new Error(data.message || (isAddMode ? "Failed to add vendor" : "Failed to update vendor quota"));
        }

        messages.push(
          data.message || (isAddMode ? "Vendor added successfully" : "Vendor quota updated successfully")
        );
      }

      showMessage(messages.join(" "), "success");
      setVendorDialogOpen(false);
      setVendorDialogMode("edit");
      setVendorDialogVendor(null);
      setAddVendorForms([emptyVendorForm()]);
      fetchVendorMetrics();
    } catch (error) {
      showMessage(error.message || (isAddMode ? "Failed to add vendor" : "Failed to update vendor quota"), "error");
    } finally {
      setQuotaSubmitLoading(false);
    }
  };

  if (!canViewProjects) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="body2" color="text">
            You do not have permission to view project country vendors.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Country Vendors"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar((current) => ({ ...current, open: false }))}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        time={3000}
      />

      <SoftBox px={0} pt="2px" pb={1} mt="-14px">
        <SoftBox display="flex" alignItems="center" gap={0.4} mb="2px">
          <SoftButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() =>
              navigate(`/projects/${id}/countries?tab=${encodeURIComponent(sourceProjectTab)}${currentProjectFilterSuffix}`)
            }
            sx={{ minWidth: "auto", px: 1.5, py: 0.55, textTransform: "none" }}
          >
            Go Back
          </SoftButton>
          <SoftTypography
            variant="h4"
            fontWeight="bold"
            color="dark"
            sx={{ fontSize: "0.875rem" }}
          >
            Vendors - {textOrDash(bid?.quest_code)} - {textOrDash(country?.name)}
          </SoftTypography>
        </SoftBox>

        <Card>
          <SoftBox px={1} pt={0.15} pb={0.2} sx={{ borderBottom: "1px solid #e9ecef" }}>
            <SoftTypography
              variant="h6"
              fontWeight="bold"
              color="dark"
              sx={{ fontSize: "1rem", mb: 0.15 }}
            >
              Overview
            </SoftTypography>
            <SoftBox
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                },
                columnGap: 3,
                rowGap: 0.15,
              }}
            >
              {overviewItems.map((item) => (
                <SoftBox key={item.label} minWidth={0}>
                  <SoftTypography variant="caption" sx={overviewLabelSx}>
                    {item.label}
                  </SoftTypography>
                  <SoftBox sx={overviewValueSx}>
                    {React.isValidElement(item.value) ? item.value : textOrDash(item.value)}
                  </SoftBox>
                </SoftBox>
              ))}
            </SoftBox>
            {(canExportProjects ||
              canAddVendor ||
              (canShowVendorLiveLinks && canCopyVendorLinks) ||
              canViewCountrySurveyResponses) && (
                <SoftBox display="flex" flexWrap="wrap" gap={0.75} mt={0.18}>
                  {canExportProjects && (
                    <SoftButton
                      color="info"
                      size="small"
                      sx={overviewButtonSx}
                      onClick={exportVendorMetrics}
                      disabled={exportLoading || filteredVendors.length === 0}
                    >
                      <Icon sx={{ mr: 0.5 }}>download</Icon>
                      {exportLoading ? "Exporting..." : "Export"}
                    </SoftButton>
                  )}
                  {canShowVendorLiveLinks && canCopyVendorLinks && (
                    <SoftButton
                      color="info"
                      size="small"
                      sx={overviewButtonSx}
                      onClick={copyVendorLinks}
                    >
                      <Icon sx={{ mr: 0.5 }}>content_copy</Icon>
                      Copy Vendor Links
                    </SoftButton>
                  )}
                  {canAddVendor && (
                    <SoftButton
                      color="info"
                      size="small"
                      sx={overviewButtonSx}
                      onClick={openAddVendorDialog}
                    >
                      <Icon sx={{ mr: 0.5 }}>person_add</Icon>
                      Add Vendor
                    </SoftButton>
                  )}
                  {canViewCountrySurveyResponses && (
                    <SoftButton
                      color="info"
                      size="small"
                      sx={overviewButtonSx}
                      onClick={() => openSurveyResponses()}
                    >
                      <Icon sx={{ mr: 0.5 }}>table_view</Icon>
                      Survey Responses
                    </SoftButton>
                  )}
                </SoftBox>
              )}
          </SoftBox>

          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
            gap={0.5}
            px="2px"
            py="2px"
          >
            <SoftBox color="text" fontSize="12px" fontWeight={400}>
              Showing {filteredVendors.length ? 1 : 0} to {filteredVendors.length} of{" "}
              {filteredVendors.length} entries
            </SoftBox>
            <SoftBox
              display="flex"
              alignItems="center"
              justifyContent={{ xs: "flex-start", sm: "flex-end" }}
              flexWrap="wrap"
              gap={0.75}
              width={{ xs: "100%", sm: "auto" }}
              ml={{ sm: "auto" }}
            >
              {(canSearchProjects ||
                canViewCreatedDate ||
                canViewStudyType ||
                canViewProjectType ||
                canViewClient) && (
                  <>
                    {canSearchProjects && (
                      <SoftBox sx={listSearchControlSx}>
                        <SoftInput
                          size="small"
                          placeholder="Search..."
                          value={searchInput}
                          onChange={(event) => setSearchInput(event.target.value)}
                          sx={listSearchInputSx}
                        />
                      </SoftBox>
                    )}
                    {canViewCreatedDate && (
                      <>
                        <SoftBox sx={listSearchControlSx}>
                          <SoftInput
                            size="small"
                            type="date"
                            value={listStartDate}
                            onChange={(event) => setListStartDate(event.target.value)}
                            sx={listSearchInputSx}
                            inputProps={{ "aria-label": "Start Date" }}
                          />
                        </SoftBox>
                        <SoftBox sx={listSearchControlSx}>
                          <SoftInput
                            size="small"
                            type="date"
                            value={listEndDate}
                            onChange={(event) => setListEndDate(event.target.value)}
                            sx={listSearchInputSx}
                            inputProps={{ "aria-label": "End Date" }}
                          />
                        </SoftBox>
                      </>
                    )}
                    {canViewStudyType && (
                      <SoftBox sx={listSearchControlSx}>
                        <SoftSelect
                          size="small"
                          compactMenu
                          placeholder="Study Type"
                          value={studyTypeFilter}
                          options={studyTypeFilterOptions}
                          onChange={setStudyTypeFilter}
                          isSearchable
                          isClearable
                          isMulti
                          sx={listSearchSelectSx}
                        />
                      </SoftBox>
                    )}
                    {canViewProjectType && (
                      <SoftBox sx={listSearchControlSx}>
                        <SoftSelect
                          size="small"
                          compactMenu
                          placeholder="Project Type"
                          value={projectTypeFilter}
                          options={projectTypeFilterOptions}
                          onChange={setProjectTypeFilter}
                          isSearchable
                          isClearable
                          isMulti
                          sx={listSearchSelectSx}
                        />
                      </SoftBox>
                    )}
                    {canViewClient && (
                      <SoftBox sx={listSearchControlSx}>
                        <SoftSelect
                          size="small"
                          compactMenu
                          placeholder="Client"
                          value={clientFilter}
                          options={clientFilterOptions}
                          isLoading={projectClientFilterOptionsLoading}
                          onChange={setClientFilter}
                          isSearchable
                          isClearable
                          isMulti
                          sx={listSearchSelectSx}
                        />
                      </SoftBox>
                    )}
                  </>
                )}
            </SoftBox>
          </SoftBox>

          {loading ? (
            <SoftBox display="flex" justifyContent="center" alignItems="center" height="260px">
              <FlagLoader />
              <SoftTypography variant="body2" color="text" ml={1}>
                Loading vendor metrics...
              </SoftTypography>
            </SoftBox>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "none",
                overflowX: "hidden",
                width: "100%",
                borderTop: "1px solid #edf2f7",
              }}
            >
              <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    {canViewVendors && <TableCell rowSpan={2} sx={tableHeaderCellSx} width="10.5%">
                      <ProjectTableSortLabel label="Vendor" sortKey="vendor" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewRequiredN && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="5.5%">
                      <ProjectTableSortLabel label="Required N" sortKey="required_n" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewVendorQuota && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="5.5%">
                      <ProjectTableSortLabel label="Vendor Completes Quota (Cap)" sortKey="vendor_quota" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewVendorQuota && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="5.5%">
                      <ProjectTableSortLabel label="Vendor Clicks Quota (Cap)" sortKey="vendor_clicks_quota" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewIr && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3.5%">
                      <ProjectTableSortLabel label="IR(%)" sortKey="ir" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewLoi && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3.5%">
                      <ProjectTableSortLabel label="LOI(mins)" sortKey="loi" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewCpi && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="4.5%">
                      <ProjectTableSortLabel label="CPI" sortKey="cpi" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewCompleteDeviceBrowser && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="7.5%">
                      <ProjectTableSortLabel label="Device/Browser" sortKey="device_browser" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewStats && <TableCell
                      colSpan={5}
                      width="19.75%"
                      sx={{ ...metricHeaderCellSx, backgroundColor: "#fde047", color: "#1e293b" }}
                    >
                      Stats
                    </TableCell>}
                    {canViewRtr && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="4.25%">
                      <ProjectTableSortLabel label="V.M" sortKey="vm" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {showEpc && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="4.25%">
                      <ProjectTableSortLabel label="EPC" sortKey="epc" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canViewRevenue && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="5.25%">
                      <ProjectTableSortLabel label="Revenue" sortKey="revenue" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {canShowVendorLiveLinks && (
                      <TableCell
                        rowSpan={2}
                        sx={{ ...metricHeaderCellSx, whiteSpace: "nowrap", overflowWrap: "normal" }}
                        width="5%"
                      >
                        <ProjectTableSortLabel label="Live Url" sortKey="links" sortConfig={sortConfig} onSort={handleSort} />
                      </TableCell>
                    )}
                    {canViewStatus && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="5.25%">
                      <ProjectTableSortLabel label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} />
                    </TableCell>}
                    {showVendorRowActions && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="8.5%">
                      <SoftBox display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                        {[
                          canViewComments ? "comment" : null,
                          projectPermissions.canAction("mail") ? "mail" : null,
                          projectPermissions.canAction("chat") ? "chat" : null,
                          canViewVendorSurveyResponses ? "keyboard_arrow_down" : null,
                        ].filter(Boolean).map((icon) => (
                          <Icon key={icon} sx={{ fontSize: "0.85rem !important" }}>
                            {icon}
                          </Icon>
                        ))}
                      </SoftBox>
                    </TableCell>}
                    {showVendorStatusActions && <TableCell
                      rowSpan={2}
                      sx={{ ...metricHeaderCellSx, px: 0, whiteSpace: "nowrap", overflowWrap: "normal" }}
                      width="1.75%"
                    >
                      <Icon sx={{ fontSize: "0.95rem !important" }}>more_vert</Icon>
                    </TableCell>}
                  </TableRow>
                  <TableRow>
                    {canViewStats && [
                      { label: "c/QS", key: "complete", color: "#0ea5e9" },
                      { label: "T", key: "terminate", color: "#ef4444" },
                      { label: "OQ", key: "quota_full", color: "#f59e0b" },
                      { label: "ST", key: "security_terminate", color: "#ef4444" },
                      { label: "P", key: "initiate", color: "#334155" },
                    ].map((column) => (
                      <TableCell
                        key={`stats-${column.label}`}
                        sx={{
                          ...metricHeaderCellSx,
                          color: column.color,
                          backgroundColor: "#f8fafc",
                        }}
                      >
                        <ProjectTableSortLabel label={column.label} sortKey={column.key} sortConfig={sortConfig} onSort={handleSort} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredVendors.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={Math.max(vendorColumnCount, 1)}
                        sx={tableBodyCellSx}
                        align="center"
                      >
                        No won vendors found for this country.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedVendors.map((vendor) => {
                      const stats = vendor.stats || {};
                      const isExpanded = expandedVendorIds.includes(vendor.id);

                      return (
                        <React.Fragment key={vendor.id}>
                          <TableRow hover sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                            {canViewVendors && <TableCell sx={tableBodyCellSx}>
                              <SoftTypography
                                variant="caption"
                                color="info"
                                display="block"
                                onClick={() => {
                                  if (canViewVendorSurveyResponses) openSurveyResponses(vendor.id);
                                }}
                                sx={{
                                  fontSize: "10px",
                                  fontWeight: 400,
                                  lineHeight: 1.1,
                                  cursor: canViewVendorSurveyResponses ? "pointer" : "default",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                {textOrDash(vendor.name)}
                              </SoftTypography>
                            </TableCell>}
                            {canViewRequiredN && <TableCell sx={metricBodyCellSx}>
                              {textOrDash(country?.required_n)}
                            </TableCell>}
                            {canViewVendorQuota && <TableCell sx={metricBodyCellSx}>
                              {textOrDash(vendor.vendor_quota)}
                            </TableCell>}
                            {canViewVendorQuota && <TableCell sx={metricBodyCellSx}>
                              {textOrDash(vendor.vendor_clicks_quota)}
                            </TableCell>}
                            {canViewIr && <TableCell sx={metricBodyCellSx}>
                              <IrMetric average={vendor.average_ir} configured={country?.ir} />
                            </TableCell>}
                            {canViewLoi && <TableCell sx={metricBodyCellSx}>
                              <LoiMetric average={vendor.average_loi} configured={country?.loi} />
                            </TableCell>}
                            {canViewCpi && <TableCell sx={metricBodyCellSx}>
                              {vendor.won_target_cpi === null ||
                                vendor.won_target_cpi === undefined
                                ? "-"
                                : `$${vendor.won_target_cpi}`}
                            </TableCell>}
                            {canViewCompleteDeviceBrowser && (
                              <TableCell sx={{ ...metricBodyCellSx, minWidth: 0, px: 0.05 }}>
                                <CompleteDeviceMetric
                                  metric={vendor.complete_device_metrics}
                                  total={stats.complete}
                                />
                              </TableCell>
                            )}
                            {canViewStats && (
                              <>
                                <TableCell sx={{ ...metricBodyCellSx, fontWeight: 700 }}>
                                  <StatsHoverTooltip
                                    label={String(vendor.name || "Vendor")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span" sx={{ display: "inline-flex" }}>
                                      <SoftBox component="span" sx={{ color: Number(stats.complete || 0) > 0 ? "#16a34a" : "#dc2626" }}>
                                        {Number(stats.complete || 0)}
                                      </SoftBox>
                                      <SoftBox component="span" sx={{ color: "#0ea5e9" }}>
                                        /{quotaText(vendor.vendor_quota)}
                                      </SoftBox>
                                    </SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                                <TableCell sx={{ ...metricBodyCellSx, color: "#dc2626", fontWeight: 700 }}>
                                  <StatsHoverTooltip
                                    label={String(vendor.name || "Vendor")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span">{Number(stats.terminate || 0)}</SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                                <TableCell sx={{ ...metricBodyCellSx, color: "#d97706", fontWeight: 700 }}>
                                  <StatsHoverTooltip
                                    label={String(vendor.name || "Vendor")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span">{Number(stats.quota_full || 0)}</SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                                <TableCell sx={{ ...metricBodyCellSx, color: "#dc2626", fontWeight: 700 }}>
                                  <StatsHoverTooltip
                                    label={String(vendor.name || "Vendor")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span">
                                      {Number(stats.security_terminate || 0)}
                                    </SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                                <TableCell sx={{ ...metricBodyCellSx, color: "#334155", fontWeight: 700 }}>
                                  <StatsHoverTooltip
                                    label={String(vendor.name || "Vendor")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span">{Number(stats.initiate || 0)}</SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                              </>
                            )}
                            {canViewRtr && <TableCell sx={{ ...metricBodyCellSx, backgroundColor: "#fee2e2" }}>
                              {percentageText(vendor.rtr)}
                            </TableCell>}
                            {showEpc && <TableCell sx={{ ...metricBodyCellSx, backgroundColor: "#e0f2fe" }}>
                              {epcText(vendor.revenue, vendor.breakdown, stats)}
                            </TableCell>}
                            {canViewRevenue && <TableCell sx={{ ...metricBodyCellSx, color: "#15803d" }}>
                              {currencyText(vendor.revenue)}
                            </TableCell>}
                            {canShowVendorLiveLinks && (
                              <TableCell sx={metricBodyCellSx}>
                                <Tooltip
                                  title={
                                    vendor.live_url || vendor.test_url
                                      ? "Show live and test Url"
                                      : "Live/test Url not available"
                                  }
                                >
                                  <span>
                                    <SoftButton
                                      variant="outlined"
                                      color="info"
                                      size="small"
                                      disabled={!vendor.live_url && !vendor.test_url}
                                      onClick={() => openVendorLinksDialog(vendor)}
                                      sx={{
                                        minWidth: 0,
                                        height: 20,
                                        minHeight: "20px !important",
                                        px: 0.45,
                                        py: 0,
                                        fontSize: "9px",
                                        lineHeight: 1,
                                        whiteSpace: "nowrap",
                                        textTransform: "none",
                                      }}
                                    >
                                      Live Url
                                    </SoftButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            )}
                            {canViewStatus && <TableCell sx={metricBodyCellSx}>{vendorStatusBadge(vendor)}</TableCell>}
                            {showVendorRowActions && <TableCell sx={metricBodyCellSx}>
                              <SoftBox display="flex" justifyContent="center" alignItems="center" gap={0.15}>
                                {canViewComments && <Tooltip title={bid?.comments || "No comments"}>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    sx={{ p: 0.2 }}
                                    onClick={() => openVendorCommunicationDialog("comment", vendor)}
                                  >
                                    <Icon sx={{ fontSize: "0.85rem !important" }}>comment</Icon>
                                  </IconButton>
                                </Tooltip>}
                                {projectPermissions.canAction("mail") && <Tooltip title="Mail">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    sx={{ p: 0.2 }}
                                    onClick={() => openVendorCommunicationDialog("mail", vendor)}
                                  >
                                    <Icon sx={{ fontSize: "0.85rem !important" }}>mail</Icon>
                                  </IconButton>
                                </Tooltip>}
                                {canUseProjectLiveChat && <Tooltip title="Project live chat">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    sx={{ p: 0.2 }}
                                    onClick={() =>
                                      openProjectLiveChat({
                                        id: bid?.id || id,
                                        quest_code: bid?.quest_code || bid?.project_code || id,
                                        project_title:
                                          bid?.project_title || bid?.project_name || bid?.quest_code || "",
                                      })
                                    }
                                  >
                                    <ProjectLiveChatBadge projectId={bid?.id || id}>
                                      <Icon sx={{ fontSize: "0.85rem !important" }}>forum</Icon>
                                    </ProjectLiveChatBadge>
                                  </IconButton>
                                </Tooltip>}
                                {canEditVendorDetails && vendor.country_win_id && (
                                  <Tooltip title="Edit vendor details">
                                    <IconButton
                                      size="small"
                                      onClick={() => openVendorQuotaDialog(vendor)}
                                      sx={{ p: 0.2, color: "#344767" }}
                                    >
                                      <Icon sx={{ fontSize: "0.85rem !important" }}>edit</Icon>
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canViewVendorSurveyResponses && <Tooltip title={isExpanded ? "Collapse details" : "Expand details"}>
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => toggleVendorDetails(vendor.id)}
                                    sx={{ p: 0.2 }}
                                  >
                                    <Icon sx={{ fontSize: "0.9rem !important" }}>
                                      {isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                                    </Icon>
                                  </IconButton>
                                </Tooltip>}
                              </SoftBox>
                            </TableCell>}
                            {showVendorStatusActions && <TableCell sx={{ ...metricBodyCellSx, px: 0 }}>
                              {canUpdateVendorStatus ? (
                                <Tooltip title="Vendor status actions">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={statusLoading || !vendor.country_win_id}
                                      onClick={(event) => openVendorActionMenu(event, vendor)}
                                      sx={{ p: 0.25 }}
                                    >
                                      <Icon sx={{ fontSize: "0.95rem !important" }}>more_vert</Icon>
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : (
                                "-"
                              )}
                            </TableCell>}
                          </TableRow>
                          {isExpanded && canViewVendorSurveyResponses && (
                            <TableRow>
                              <TableCell colSpan={Math.max(vendorColumnCount, 1)} sx={{ p: 0, borderBottom: "1px solid #cbd5e1" }}>
                                <SoftBox
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 2fr" },
                                    gap: 1,
                                    p: 1,
                                    backgroundColor: "#e5e7eb",
                                  }}
                                >
                                  {canViewVendors && <SoftBox>
                                    <SoftTypography
                                      variant="caption"
                                      color="dark"
                                      sx={{ fontSize: "12px", fontWeight: 400 }}
                                    >
                                      Vendor
                                    </SoftTypography>
                                    <SoftTypography
                                      variant="body2"
                                      color="dark"
                                      sx={{ fontSize: "10px", fontWeight: 400 }}
                                    >
                                      {textOrDash(vendor.company || vendor.name)}
                                    </SoftTypography>
                                  </SoftBox>}
                                  {canViewCountry && <SoftBox>
                                    <SoftTypography
                                      variant="caption"
                                      color="dark"
                                      sx={{ fontSize: "12px", fontWeight: 400 }}
                                    >
                                      Country
                                    </SoftTypography>
                                    <SoftTypography
                                      variant="body2"
                                      color="dark"
                                      sx={{ fontSize: "10px", fontWeight: 400 }}
                                    >
                                      {textOrDash(country?.name)}
                                    </SoftTypography>
                                  </SoftBox>}
                                  {canViewTargeting && <SoftBox>
                                    <SoftTypography
                                      variant="caption"
                                      color="dark"
                                      sx={{ fontSize: "12px", fontWeight: 400 }}
                                    >
                                      Targeting
                                    </SoftTypography>
                                    <SoftTypography
                                      variant="body2"
                                      color="dark"
                                      sx={{ fontSize: "10px", fontWeight: 400 }}
                                    >
                                      {textOrDash(country?.targeting)}
                                    </SoftTypography>
                                  </SoftBox>}
                                </SoftBox>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        <Dialog
          open={vendorDialogOpen}
          onClose={closeVendorQuotaDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: "8px" } }}
        >
          <DialogTitle sx={{ fontSize: "18px", py: 1.25 }}>
            {vendorDialogMode === "add" ? "Add Vendor" : "Edit Vendor Details"}
          </DialogTitle>
          <DialogContent sx={{ pt: 0.5, pb: 1 }}>
            <SoftBox display="grid" gap={1.25}>
              <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1}>
                {canViewProjectCode && (
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold" color="dark">
                      Project Code
                    </SoftTypography>
                    <SoftTypography variant="body2" color="text" sx={{ fontSize: "12px" }}>
                      {textOrDash(bid?.quest_code)}
                    </SoftTypography>
                  </SoftBox>
                )}
                {canViewCountry && (
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold" color="dark">
                      Country
                    </SoftTypography>
                    <SoftTypography variant="body2" color="text" sx={{ fontSize: "12px" }}>
                      {textOrDash(country?.name)}
                    </SoftTypography>
                  </SoftBox>
                )}
              </SoftBox>

              {vendorDialogMode === "add" ? (
                <>
                  {addVendorForms.map((form, index) => (
                    <SoftBox
                      key={`country-vendor-form-${index + 1}`}
                      sx={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        p: 1,
                        display: "grid",
                        gap: 1,
                      }}
                    >
                      <SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" color="dark">
                          Vendor {index + 1}
                        </SoftTypography>
                        {addVendorForms.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => removeVendorFormRow(index)}
                            sx={{ p: 0.25, color: "#ef4444" }}
                          >
                            <Icon sx={{ fontSize: "0.95rem !important" }}>delete</Icon>
                          </IconButton>
                        )}
                      </SoftBox>

                      <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }} gap={1}>
                        {canViewVendors && (
                          <SoftBox gridColumn={{ xs: "1", md: "1 / -1" }}>
                            <SoftTypography variant="caption" fontWeight="bold" color="dark">
                              Vendor
                            </SoftTypography>
                            <SoftSelect
                              compact
                              size="small"
                              placeholder="Select Vendor"
                              value={form.vendor}
                              options={vendorOptions}
                              isLoading={vendorOptionsLoading}
                              onMenuOpen={fetchVendorOptions}
                              onChange={(value) => updateAddVendorForm(index, "vendor", value)}
                              isSearchable
                              isClearable
                            />
                          </SoftBox>
                        )}
                        {canViewVendorQuota && (
                          <SoftBox>
                            <SoftTypography variant="caption" fontWeight="bold" color="dark">
                              Vendor Completes Quota (Cap)
                            </SoftTypography>
                            <SoftInput
                              size="small"
                              value={form.vendor_quota}
                              placeholder="Vendor Completes Quota (Cap)"
                              onChange={(event) =>
                                updateAddVendorForm(index, "vendor_quota", event.target.value)
                              }
                            />
                          </SoftBox>
                        )}
                        {canViewVendorQuota && (
                          <SoftBox>
                            <SoftTypography variant="caption" fontWeight="bold" color="dark">
                              Vendor Clicks Quota (Cap)
                            </SoftTypography>
                            <SoftInput
                              size="small"
                              value={form.vendor_clicks_quota}
                              placeholder="Vendor Clicks Quota (Cap)"
                              onChange={(event) =>
                                updateAddVendorForm(index, "vendor_clicks_quota", event.target.value)
                              }
                            />
                          </SoftBox>
                        )}
                        {canViewTargetCpi && (
                          <SoftBox>
                            <SoftTypography variant="caption" fontWeight="bold" color="dark">
                              Target CPI
                            </SoftTypography>
                            <SoftInput
                              size="small"
                              value={form.target_cpi}
                              placeholder="Target CPI"
                              onChange={(event) =>
                                updateAddVendorForm(index, "target_cpi", event.target.value)
                              }
                            />
                          </SoftBox>
                        )}
                        {canManageVendorLinkHitGap && (
                          <SoftBox>
                            <SoftTypography variant="caption" fontWeight="bold" color="dark">
                              Vendor Link Hit Gap (Seconds)
                            </SoftTypography>
                            <SoftInput
                              size="small"
                              type="number"
                              value={form.link_hit_gap_seconds}
                              placeholder="Default 60"
                              inputProps={{ min: 1, max: 86400, step: 1 }}
                              onChange={(event) =>
                                updateAddVendorForm(
                                  index,
                                  "link_hit_gap_seconds",
                                  event.target.value
                                )
                              }
                            />
                          </SoftBox>
                        )}
                        <SoftBox gridColumn={{ xs: "1", md: "1 / -1" }}>
                          <FormControlLabel
                            sx={{ m: 0, alignItems: "center", gap: 0.5 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={Boolean(form.is_verisoul_security)}
                                onChange={(event) =>
                                  updateAddVendorForm(
                                    index,
                                    "is_verisoul_security",
                                    event.target.checked
                                  )
                                }
                                sx={{
                                  p: 0,
                                  color: "#d2d6da",
                                  "&.Mui-checked": { color: "#17c1e8" },
                                }}
                              />
                            }
                            label={
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                                sx={{ color: "#17c1e8" }}
                              >
                                Enable Verisoul Security
                              </SoftTypography>
                            }
                          />
                        </SoftBox>
                      </SoftBox>

                      {canViewVendorRedirectDetails && (
                        <>
                          <SoftTypography variant="caption" fontWeight="bold" color="dark">
                            Redirect Details
                          </SoftTypography>
                          <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }} gap={1}>
                            {vendorRedirectFields.map((field) => (
                              <SoftBox key={field.key}>
                                <SoftTypography variant="caption" fontWeight="bold" color="dark">
                                  {field.label}
                                </SoftTypography>
                                <SoftInput
                                  size="small"
                                  value={form[field.key]}
                                  placeholder="https://vendor.com/survey?status=1&rid=[identifier]"
                                  onChange={(event) =>
                                    updateAddVendorForm(index, field.key, event.target.value)
                                  }
                                />
                              </SoftBox>
                            ))}
                          </SoftBox>
                        </>
                      )}
                    </SoftBox>
                  ))}
                  {canSubmitAddVendor && (
                    <SoftBox display="flex" justifyContent="flex-end">
                      <SoftButton
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={addVendorFormRow}
                        sx={{
                          px: 1.25,
                          py: 0.45,
                          minHeight: 28,
                          fontSize: "11px",
                          textTransform: "none",
                        }}
                      >
                        Add More
                      </SoftButton>
                    </SoftBox>
                  )}
                </>
              ) : (
                <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1}>
                  {canViewVendors && (
                    <SoftBox gridColumn={{ xs: "1", sm: "1 / -1" }}>
                      <SoftTypography variant="caption" fontWeight="bold" color="dark">
                        Vendor
                      </SoftTypography>
                      <SoftTypography variant="body2" color="text" sx={{ fontSize: "12px" }}>
                        {textOrDash(vendorDialogVendor?.name)}
                      </SoftTypography>
                    </SoftBox>
                  )}
                  {canViewVendorQuota && (
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold" color="dark">
                        Vendor Completes Quota (Cap)
                      </SoftTypography>
                      <SoftInput
                        size="small"
                        value={vendorForm.vendor_quota}
                        placeholder="Vendor Completes Quota (Cap)"
                        onChange={(event) => updateVendorForm("vendor_quota", event.target.value)}
                      />
                    </SoftBox>
                  )}
                  {canViewVendorQuota && (
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold" color="dark">
                        Vendor Clicks Quota (Cap)
                      </SoftTypography>
                      <SoftInput
                        size="small"
                        value={vendorForm.vendor_clicks_quota}
                        placeholder="Vendor Clicks Quota (Cap)"
                        onChange={(event) =>
                          updateVendorForm("vendor_clicks_quota", event.target.value)
                        }
                      />
                    </SoftBox>
                  )}
                  {canViewTargetCpi && (
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold" color="dark">
                        Target CPI
                      </SoftTypography>
                      <SoftInput
                        size="small"
                        value={vendorForm.target_cpi}
                        placeholder="Target CPI"
                        onChange={(event) => updateVendorForm("target_cpi", event.target.value)}
                      />
                    </SoftBox>
                  )}
                  {canManageVendorLinkHitGap && (
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold" color="dark">
                        Vendor Link Hit Gap (Seconds)
                      </SoftTypography>
                      <SoftInput
                        size="small"
                        type="number"
                        value={vendorForm.link_hit_gap_seconds}
                        placeholder="NULL"
                        inputProps={{ min: 1, max: 86400, step: 1 }}
                        onChange={(event) =>
                          updateVendorForm("link_hit_gap_seconds", event.target.value)
                        }
                      />
                    </SoftBox>
                  )}
                  <SoftBox gridColumn={{ xs: "1", sm: "1 / -1" }}>
                    <FormControlLabel
                      sx={{ m: 0, alignItems: "center" }}
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(vendorForm.is_verisoul_security)}
                          onChange={(event) =>
                            updateVendorForm("is_verisoul_security", event.target.checked)
                          }
                        />
                      }
                      label={
                        <SoftTypography variant="caption" color="dark">
                          <strong> Enable Verisoul security </strong>
                        </SoftTypography>
                      }
                    />
                  </SoftBox>
                  {canViewFeasibility && (
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold" color="dark">
                        Feasibility
                      </SoftTypography>
                      <SoftInput
                        size="small"
                        value={vendorForm.feasibility}
                        placeholder="Feasibility"
                        onChange={(event) => updateVendorForm("feasibility", event.target.value)}
                      />
                    </SoftBox>
                  )}
                  {canViewRequiredFieldTime && (
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold" color="dark">
                        Required Field Time
                      </SoftTypography>
                      <SoftInput
                        size="small"
                        value={vendorForm.required_field_time_days}
                        placeholder="Required Field Time"
                        onChange={(event) =>
                          updateVendorForm("required_field_time_days", event.target.value)
                        }
                      />
                    </SoftBox>
                  )}
                  {canViewVendorRedirectDetails && (
                    <>
                      <SoftBox gridColumn={{ xs: "1", sm: "1 / -1" }}>
                        <SoftTypography variant="caption" fontWeight="bold" color="dark">
                          Redirect Details
                        </SoftTypography>
                      </SoftBox>
                      {vendorRedirectFields.map((field) => (
                        <SoftBox key={field.key}>
                          <SoftTypography variant="caption" fontWeight="bold" color="dark">
                            {field.label}
                          </SoftTypography>
                          <SoftInput
                            size="small"
                            value={vendorForm[field.key]}
                            placeholder="https://vendor.com/survey?status=1&rid=[identifier]"
                            onChange={(event) => updateVendorForm(field.key, event.target.value)}
                          />
                        </SoftBox>
                      ))}
                    </>
                  )}
                </SoftBox>
              )}
            </SoftBox>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 1.5, pt: 0 }}>
            <SoftButton variant="outlined" color="dark" size="small" onClick={closeVendorQuotaDialog}>
              Close
            </SoftButton>
            {canSubmitVendorDialog && (
              <SoftButton
                variant="gradient"
                color="info"
                size="small"
                disabled={quotaSubmitLoading}
                onClick={submitVendorQuota}
              >
                {quotaSubmitLoading ? "Saving..." : "Save"}
              </SoftButton>
            )}
          </DialogActions>
        </Dialog>

        <Dialog
          open={linkDialogOpen}
          onClose={closeVendorLinksDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: "8px" } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: "18px",
              py: 1.25,
            }}
          >
            Live Url
            <IconButton size="small" onClick={closeVendorLinksDialog}>
              <Icon>close</Icon>
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 0.5, pb: 1 }}>
            <SoftBox display="grid" gap={1.25}>
              {canViewVendors && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Vendor
                  </SoftTypography>
                  <SoftTypography variant="body2" color="text" sx={{ fontSize: "12px" }}>
                    {textOrDash(linkDialogVendor?.name)}
                  </SoftTypography>
                </SoftBox>
              )}
              {canViewLiveUrl && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Live Url
                  </SoftTypography>
                  <SoftInput
                    multiline
                    rows={2}
                    value={linkDialogVendor?.live_url || ""}
                    inputProps={{ readOnly: true }}
                    sx={{
                      "& textarea": {
                        fontSize: "13px",
                        lineHeight: 1.35,
                      },
                    }}
                  />
                  {canCopyVendorUrl && (
                    <SoftButton
                      variant="gradient"
                      color="info"
                      size="small"
                      onClick={() => copyLink(linkDialogVendor?.live_url, "Live")}
                      sx={{ mt: 0.75, px: 1.25 }}
                    >
                      <Icon sx={{ fontSize: "1rem !important", mr: 0.35 }}>content_copy</Icon>
                      Copy
                    </SoftButton>
                  )}
                </SoftBox>
              )}
              {canViewTestUrl && (
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Test Url
                  </SoftTypography>
                  <SoftInput
                    multiline
                    rows={2}
                    value={linkDialogVendor?.test_url || ""}
                    inputProps={{ readOnly: true }}
                    sx={{
                      "& textarea": {
                        fontSize: "13px",
                        lineHeight: 1.35,
                      },
                    }}
                  />
                  {canCopyVendorUrl && (
                    <SoftButton
                      variant="gradient"
                      color="secondary"
                      size="small"
                      onClick={() => copyLink(linkDialogVendor?.test_url, "Test")}
                      sx={{ mt: 0.75, px: 1.25 }}
                    >
                      <Icon sx={{ fontSize: "1rem !important", mr: 0.35 }}>content_copy</Icon>
                      Copy
                    </SoftButton>
                  )}
                </SoftBox>
              )}
            </SoftBox>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 1.5, pt: 0 }}>
            <SoftButton variant="outlined" color="dark" size="small" onClick={closeVendorLinksDialog}>
              Close
            </SoftButton>
          </DialogActions>
        </Dialog>

        <ProjectCommunicationComposerDialog
          open={Boolean(communicationDialog)}
          type={communicationDialog?.type}
          scope={communicationDialog?.scope}
          onClose={() => setCommunicationDialog(null)}
          onSent={fetchVendorMetrics}
          onNotify={showMessage}
        />

        <Menu
          anchorEl={actionsAnchor}
          open={Boolean(actionsAnchor)}
          onClose={closeVendorActionMenu}
          PaperProps={{
            sx: {
              minWidth: 140,
              borderRadius: "8px",
              boxShadow: "0 12px 32px rgba(20, 20, 43, 0.18)",
              py: 1,
            },
          }}
        >
          {vendorStatusActions.map((action) => {
            const currentStatus =
              String(actionVendor?.status || "active").toLowerCase() === "inactive"
                ? "inactive"
                : "active";
            const isCurrent = currentStatus === action.value;

            return (
              <MenuItem
                key={action.value}
                disabled={statusLoading || isCurrent}
                onClick={() => updateVendorStatus(action.value)}
                sx={{ color: action.color, fontWeight: 600, fontSize: "0.875rem" }}
              >
                {action.label}
                {statusLoading && !isCurrent ? (
                  <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />
                ) : null}
              </MenuItem>
            );
          })}
        </Menu>
      </SoftBox>

      <Footer />
    </DashboardLayout>
  );
}

export default ProjectCountryVendors;
